import { createApi, fakeBaseQuery } from "@reduxjs/toolkit/query/react";
import http from "../services/http";

const ORDER_INDEPENDENT_ARRAY_KEYS = new Set([
  "category",
  "categories",
  "subcategory",
  "subcategories",
  "tender_categories",
  "tender_subcategory",
  "product",
  "products",
  "keyword",
  "keywords",
  "department",
  "departments",
  "ownership",
  "state",
  "states",
  "zone",
  "zones",
  "city",
  "cities",
  "report_sections",
]);

function normalizeCacheValue(value, parentKey = "") {
  if (value == null) return value;

  if (typeof value === "string") return value.trim();

  if (Array.isArray(value)) {
    const normalized = value.map((item) => normalizeCacheValue(item));
    if (
      ORDER_INDEPENDENT_ARRAY_KEYS.has(parentKey) &&
      normalized.every(
        (item) =>
          item == null ||
          typeof item === "string" ||
          typeof item === "number" ||
          typeof item === "boolean",
      )
    ) {
      return normalized.slice().sort((left, right) =>
        String(left ?? "").localeCompare(String(right ?? "")),
      );
    }
    return normalized;
  }

  if (typeof value === "object") {
    return Object.keys(value)
      .sort()
      .reduce((result, key) => {
        const normalized = normalizeCacheValue(value[key], key);
        if (normalized !== undefined) result[key] = normalized;
        return result;
      }, {});
  }

  return value;
}

function serializeAxiosError(error) {
  const status = error?.response?.status ?? error?.code ?? "FETCH_ERROR";
  const responseData = error?.response?.data;
  const message =
    responseData?.message ||
    responseData?.detail ||
    error?.message ||
    "Tender 360 request failed";

  return {
    status,
    data: responseData ?? { message: String(message) },
    message: typeof message === "string" ? message : JSON.stringify(message),
  };
}

const executeAxiosRequest = async (args, api) => {
  try {
    const response = await http.request({
      method: args?.method || "post",
      url: args?.url,
      data: args?.data,
      params: args?.params,
      signal: api.signal,
    });
    return { data: response?.data };
  } catch (error) {
    return { error: serializeAxiosError(error) };
  }
};

const serializeTender360QueryArgs = ({ endpointName, queryArgs }) => {
  const cacheIdentity = normalizeCacheValue({
    method: String(queryArgs?.method || "post").toLowerCase(),
    url: queryArgs?.url || "",
    data: queryArgs?.data,
    params: queryArgs?.params,
    scopeKey: queryArgs?.scopeKey || "guest",
  });
  return `${endpointName}:${JSON.stringify(cacheIdentity)}`;
};

export const tender360Api = createApi({
  reducerPath: "tender360Api",
  baseQuery: fakeBaseQuery(),
  keepUnusedDataFor: 1800,
  endpoints: (builder) => ({
    request: builder.query({
      queryFn: executeAxiosRequest,
      serializeQueryArgs: serializeTender360QueryArgs,
      keepUnusedDataFor: 1800,
    }),
    lookupRequest: builder.query({
      queryFn: executeAxiosRequest,
      serializeQueryArgs: serializeTender360QueryArgs,
      keepUnusedDataFor: 300,
    }),
  }),
});
